import { useEffect } from 'react'

import { useAuthStore } from '@/features/auth/stores/auth.store'
import socketService from '@/shared/socket/socket.service'
import { useNotificationStore } from '@/shared/stores/notification.store'

import { useCommentsStore } from '../stores/comments.store'

export const useCommentNotifications = () => {
  const role = useAuthStore((state) => state.user?.role)

  const addCommentNotification = useCommentsStore(
    (state) => state.addNotification,
  )

  const fetchNotifications = useCommentsStore(
    (state) => state.fetchNotifications,
  )

  useEffect(() => {
    if (
      role !== 'admin' &&
      role !== 'sales' &&
      role !== 'marketing' &&
      role !== 'purchase'
    ) {
      return undefined
    }

    fetchNotifications()
      .then((fetchedList) => {
        if (Array.isArray(fetchedList)) {
          // Check if unread comments exceed 9 for any lead
          const unreadByLead = {}
          fetchedList.forEach((item) => {
            if (!item.isRead && item.leadId) {
              unreadByLead[item.leadId] = (unreadByLead[item.leadId] || 0) + 1
            }
          })

          // Only if unread comments exceed 9 (> 9, reaching threshold 10), deliver to notification drawer
          Object.entries(unreadByLead).forEach(([leadId, count]) => {
            if (count > 9) {
              useNotificationStore.getState().addNotification({
                id: `comment-reminder-${leadId}`,
                title: 'Pending Comments',
                body: `Pending Comments: You have ${count} unread comments`,
                type: 'comment_reminder',
                data: { leadId, unreadCount: count },
                read: false,
                createdAt: new Date().toISOString(),
              })
            }
          })
        }
      })
      .catch((error) => {
        console.error('Failed to fetch comment notifications:', error)
      })

    const socket = socketService.connect()

    if (!socket) {
      return undefined
    }

    const handleNotification = (notification) => {
      // 1. Always update comment store so comment icon badge in table rows shows the exact count
      addCommentNotification(notification)

      // 2. Check if unread comments exceed 9 (> 9, reaching threshold 10)
      const currentNotifications =
        useCommentsStore.getState().notifications || []
      const leadId = notification.leadId
      const unreadCountForLead = currentNotifications.filter(
        (n) => n.leadId === leadId && !n.isRead,
      ).length

      if (unreadCountForLead > 9) {
        useNotificationStore.getState().addNotification({
          id: `comment-reminder-${leadId}`,
          title: 'Pending Comments',
          body: `Pending Comments: You have ${unreadCountForLead} unread comments`,
          type: 'comment_reminder',
          data: {
            leadId,
            unreadCount: unreadCountForLead,
          },
          read: false,
          createdAt: notification.createdAt || new Date().toISOString(),
        })
      }
    }

    socket.on('comment:notification', handleNotification)

    return () => {
      socket.off('comment:notification', handleNotification)
    }
  }, [role, fetchNotifications, addCommentNotification])
}
